import { getNormalizedUserPermissions } from "./permissionModel.js";
import { listUserAccessChangeDraftsCallable, applyUserAccessChangeCallable } from "../../app/firebase/callables.js";
import { applyConfirmedUserSettings, describeUserAccessEdit } from "./userAccessSettings.js";
import { saveUserDeactivationDraft } from "./userLifecycleApi.js";
/**
 * @file Users settings deactivation draft rendering and actions.
 */

import {
  appData,
  state,
  tempUsersData,
  setTempUsersData,
  tempUserDeactivationDraft,
  setTempUserDeactivationDraft,
  clearTempUserDeactivationDraft,
  deactivationDraftSummaries,
  setDeactivationDraftSummaries,
  clearDeactivationDraftSummaries,
} from "../../app/state/state.js";
import { navigate, navigateToDetails } from "../../app/routing/navigationController.js";
import { openConfirmationModal } from "../../shared/ui/confirmationModal.js";
import { createSelectControl } from "../../shared/ui/controls/selectControl.js";
import { escapeHTML } from "../../shared/utils/contentUtils.js";
import { formatDate } from "../../shared/utils/dateUtils.js";
import { saveSettingsChanges } from "./settingsApi.js";
import {
  deactivateUserWithDraft,
  deleteUserDeactivationDraft,
  listUserDeactivationDrafts,
  openUserDeactivationDraft,
  reactivateUser,
  refreshUserDeactivationDraft,
  runUserDeactivationMigration,
} from "./userLifecycleApi.js";

let deactivationDraftsLoading = null;
let deactivationDraftsLoaded = false;
let hiddenDeactivationDraftUserId = null;

function attrValue(value = "") {
  return escapeHTML(String(value ?? ""))
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

/**
 * Finds the active deactivation draft summary for a user.
 * @param {string} userId Target user ID.
 * @param {string} [operation] Lifecycle operation family.
 * @returns {object|null} Draft summary when one is loaded.
 */
export function getDeactivationDraftSummary(userId, operation = "deactivation") {
  return (
    (deactivationDraftSummaries || []).find(
      (draft) => draft.targetUserId === userId &&
        (draft.operation || "deactivation") === operation,
    ) || null
  );
}

/**
 * Retains a newly opened review in the existing operation-aware summary list.
 * @param {object} draftPayload Current server payload.
 * @returns {void}
 */
export function upsertDeactivationDraftSummary(draftPayload) {
  const summary = draftPayload?.draft || draftPayload;
  if (!summary?.targetUserId) {
    return;
  }

  const nextDrafts = Array.isArray(deactivationDraftSummaries)
    ? [...deactivationDraftSummaries]
    : [];
  const index = nextDrafts.findIndex(
    (draft) => draft.targetUserId === summary.targetUserId &&
      (draft.operation || "deactivation") === (summary.operation || "deactivation"),
  );

  if (summary.status && summary.status !== "active") {
    if (index !== -1) {
      nextDrafts.splice(index, 1);
    }
  } else if (index === -1) {
    nextDrafts.push({
      id: summary.id, operation: summary.operation || "deactivation",
      targetUserId: summary.targetUserId,
      status: summary.status || "active",
      createdAt: summary.createdAt || null,
      updatedAt: summary.updatedAt || null,
      startedBy: summary.startedBy || null,
      lastUpdatedBy: summary.lastUpdatedBy || null,
    });
  } else {
    nextDrafts[index] = {
      ...nextDrafts[index],
      ...summary,
    };
  }

  nextDrafts.sort((a, b) => (b.updatedAt || 0) - (a.updatedAt || 0));
  setDeactivationDraftSummaries(nextDrafts);
  deactivationDraftsLoaded = true;
}

/**
 * Loads active user deactivation draft summaries for the Users tab.
 * @param {{ force?: boolean, rerenderUsersTab?: Function, shouldRerender?: Function }} options Load options and coordinator callbacks.
 * @returns {Promise<Array>|Array} Loaded draft summaries or the in-flight load promise.
 */
export async function ensureDeactivationDraftSummariesLoaded(options = {}) {
  const {
    force = false,
    rerenderUsersTab = () => {},
    shouldRerender = () => false,
  } = options;
  if (deactivationDraftsLoading && !force) {
    return deactivationDraftsLoading;
  }
  if (deactivationDraftsLoaded && !force) {
    return deactivationDraftSummaries;
  }

  deactivationDraftsLoading = (async () => {
    try {
      const [accountDrafts, accessResult] = await Promise.all([
        listUserDeactivationDrafts(), listUserAccessChangeDraftsCallable(),
      ]);
      const drafts = [...accountDrafts, ...(accessResult.data?.drafts || [])];
      setDeactivationDraftSummaries(drafts);
      deactivationDraftsLoaded = true;
      if (
        state.currentView === "settings" &&
        !tempUserDeactivationDraft?.draft &&
        shouldRerender()
      ) {
        rerenderUsersTab();
      }
      return drafts;
    } catch (error) {
      console.error("Failed to load deactivation draft summaries:", error);
      return [];
    } finally {
      deactivationDraftsLoading = null;
    }
  })();

  return deactivationDraftsLoading;
}

function getDraftUserName(userId) {
  const user =
    (tempUsersData || []).find(
      (entry) => entry.id === userId || entry.uid === userId,
    ) ||
    (appData.users || []).find(
      (entry) => entry.id === userId || entry.uid === userId,
    );
  if (!user) {
    return userId || "Unknown user";
  }
  const baseName = user.name || user.email || userId;
  return user.isActive === false ? `${baseName} (inactive)` : baseName;
}

function formatDraftUserList(userIds = []) {
  if (!Array.isArray(userIds) || userIds.length === 0) {
    return "None";
  }
  return userIds.map((uid) => getDraftUserName(uid)).join(", ");
}

function removeDeactivationDraftSummary(targetUserId, operation = "deactivation") {
  if (!targetUserId) {
    return;
  }
  const nextDrafts = (deactivationDraftSummaries || []).filter(
    (draft) => draft.targetUserId !== targetUserId ||
      (draft.operation || "deactivation") !== operation,
  );
  setDeactivationDraftSummaries(nextDrafts);
  deactivationDraftsLoaded = true;
}

function formatDraftDate(value) {
  if (!value) {
    return "Unknown date";
  }
  const date = typeof value === "number" ? new Date(value) : new Date(value);
  if (Number.isNaN(date.getTime())) {
    return "Unknown date";
  }
  return date.toLocaleDateString("en-AU");
}

function formatDraftDateRange(startDate, endDate = startDate) {
  const start = formatDate(startDate) || startDate || "";
  const end = formatDate(endDate || startDate) || endDate || startDate || "";
  if (!start) {
    return "";
  }
  return !end || end === start ? start : `${start} - ${end}`;
}

function formatDraftDetailsDateRange(details = {}) {
  const directRange = formatDraftDateRange(details.startDate, details.endDate);
  if (directRange) {
    return directRange;
  }
  const rawRange = String(details.dateRange || "").trim();
  if (!rawRange) {
    return "";
  }
  const parts = rawRange.split(/\s+-\s+|\s+to\s+/i);
  if (parts.length === 2) {
    return formatDraftDateRange(parts[0], parts[1]);
  }
  return formatDate(rawRange) || rawRange;
}

function getConfigOptionName(options, id) {
  const option = (Array.isArray(options) ? options : [])
    .find((entry) => String(entry?.id || "") === String(id || ""));
  return option?.name || option?.label || id;
}

function formatDraftTypeList(typeIds = [], configKey) {
  const ids = Array.isArray(typeIds) ? typeIds : [];
  if (!ids.length) {
    return "None";
  }
  const options = appData.config?.[configKey] || [];
  return ids.map((id) => getConfigOptionName(options, id)).join(", ");
}

function userHasCalendarAccess(user) {
  return (
    user?.role === "admin" ||
    (Array.isArray(user?.permissions) && user.permissions.includes("calendar"))
  );
}

function isDraftReplacementUserValidForRow(row, user, targetUserId) {
  if (!user || user.id === targetUserId || user.isActive === false) {
    return false;
  }
  if (row?.details?.permission && user.role !== "admin" &&
      !getNormalizedUserPermissions(user.role, user.permissions)
        .includes(row.details.permission)) {return false;}
  if (row?.details?.siteId && user.role !== "admin" &&
      !user.siteIds?.includes(row.details.siteId)) {return false;}
  if (row?.itemType === "recalls" && row?.field === "provider") {
    return user.isAppProvider === true;
  }
  if (row?.itemType === "calendar_event" && row?.field === "hostIds") {
    return userHasCalendarAccess(user);
  }
  if (row?.itemType === "manager_assignment" &&
    row?.field === "managerUserId") {
    return (
      user.id !== row.itemId &&
      !(
        Array.isArray(row.details?.affectedUserIds) &&
        row.details.affectedUserIds.includes(user.id)
      )
    );
  }
  return true;
}

function getDraftReplacementOptions(targetUserId, row = null) {
  return [...(tempUsersData || [])]
    .filter((user) => isDraftReplacementUserValidForRow(row, user, targetUserId))
    .sort((a, b) => (a.name || a.email || "").localeCompare(b.name || b.email || ""));
}

function getDraftSectionRows(key) {
  return tempUserDeactivationDraft?.sections?.[key] || [];
}

function getDraftSelection(rowId) {
  return tempUserDeactivationDraft?.draft?.selections?.[rowId] || null;
}

function getDraftFieldLabel(field, row) {
  const labels = {
    owner: "Owner",
    assigned_users: "Assigned users",
    case_manager: "Case manager",
    provider: row?.itemType === "approval" ? "Approver" : "Provider",
    project_manager: "Project managers",
    managed_by: "Managed by",
    defaultPatientCaseManager: "Default patient case manager",
    "maintenance.defaultManagedBy": "Default maintenance manager",
    "labs.defaultAssignedUserByStream.internal": "Default internal Lab assignee",
    "labs.defaultAssignedUserByStream.external": "Default external Lab assignee",
    members: "Team members",
    role: "Admin role",
    approverIds: "Approval",
    hostIds: "Event hosts",
    attendeeIds: "Event attendees",
    managerUserId: "Manager",
    requesterId: "Leave",
    status: row?.itemType === "leave_request" ? "Pending leave" : "Leave status",
  };
  return labels[field] || field;
}

function getDraftTileSeverity(rows = []) {
  if (
    rows.some(
      (row) =>
        doesDraftRowRequireMigration(row),
    )
  ) {
    return {
      wrapper: "border-red-200 bg-red-50/70",
      badge: "bg-red-100 text-red-700",
    };
  }

  if (
    rows.some(
      (row) =>
        Array.isArray(row.allowedActions) && row.allowedActions.length > 0,
    )
  ) {
    return {
      wrapper: "border-yellow-200 bg-yellow-50/80",
      badge: "bg-yellow-100 text-yellow-700",
    };
  }

  return {
    wrapper: "border-green-200 bg-green-50/80",
    badge: "bg-green-100 text-green-700",
  };
}

function getDraftTileSeverityRank(rows = []) {
  if (
    rows.some(
      (row) =>
        doesDraftRowRequireMigration(row),
    )
  ) {
    return 0;
  }
  if (
    rows.some(
      (row) =>
        Array.isArray(row.allowedActions) && row.allowedActions.length > 0,
    )
  ) {
    return 1;
  }
  return 2;
}

function doesDraftRowRequireMigration(row) {
  if (!row?.blocking && row?.details?.required === false) {return false;}
  return Boolean(
    row?.blocking ||
      row?.allowedActions?.includes("clear") ||
      (row?.allowedActions?.includes("replace") && !isRemoveActionAllowed(row)),
  );
}

function formatParentTypeLabel(parentType) {
  if (!parentType) {
    return "Parent";
  }
  return parentType.charAt(0).toUpperCase() + parentType.slice(1);
}

function getDraftActionValue(row) {
  const rowId = row?.rowId;
  const selection = getDraftSelection(rowId);
  const defaultReplacementUserId =
    tempUserDeactivationDraft?.draft?.defaultReplacementUserId || null;
  const defaultReplacementUser =
    (tempUsersData || []).find((user) => user.id === defaultReplacementUserId) ||
    null;
  const hasValidDefaultReplacement =
    defaultReplacementUserId &&
    isDraftReplacementUserValidForRow(
      row,
      defaultReplacementUser,
      tempUserDeactivationDraft?.draft?.targetUserId || null,
    );

  if (!selection) {
    if (row?.allowedActions?.includes("replace") && hasValidDefaultReplacement) {
      return "__default__";
    }
    return "__leave__";
  }

  if (selection.action === "replace") {
    if (!selection.replacementUserId) {
      return hasValidDefaultReplacement ? "__default__" : "__leave__";
    }
    const selectedReplacementUser =
      (tempUsersData || []).find((user) => user.id === selection.replacementUserId) ||
      null;
    return isDraftReplacementUserValidForRow(
      row,
      selectedReplacementUser,
      tempUserDeactivationDraft?.draft?.targetUserId || null,
    )
      ? selection.replacementUserId
      : "__leave__";
  }
  if (selection.action === "remove") {
    return "__remove__";
  }
  if (selection.action === "clear") {
    return "__clear__";
  }
  if (selection.action === "leave_unresolved") {
    return "__leave__";
  }
  if (row?.allowedActions?.includes("replace") && hasValidDefaultReplacement) {
    return "__default__";
  }
  return "__leave__";
}

function getDraftExecutableRowCount() {
  const sections = tempUserDeactivationDraft?.sections || {};
  return Object.values(sections)
    .flat()
    .filter((row) => {
      const value = getDraftActionValue(row);
      return value !== "__leave__";
    }).length;
}

function getDraftReplacementSelectOptions(targetUserId) {
  return [
    { value: "", label: "No default replacement" },
    ...getDraftReplacementOptions(targetUserId).map((user) => ({
      value: user.id,
      label: user.name || user.email || user.id,
    })),
  ];
}

function isRemoveActionAllowed(row) {
  if (!Array.isArray(row?.allowedActions)) {
    return false;
  }
  if (row.allowedActions.includes("remove")) {
    return true;
  }
  if (row.allowedActions.includes("remove_if_others_remain")) {
    return (
      Array.isArray(row.activeRemainingUserIds) &&
      row.activeRemainingUserIds.length > 0
    );
  }
  return false;
}

function renderDeactivationDraftActionControls(row, targetUserId) {
  const allowReplace = row.allowedActions?.includes("replace");
  const allowRemove = isRemoveActionAllowed(row);
  const allowClear = row.allowedActions?.includes("clear");
  const replacementOptions = getDraftReplacementOptions(targetUserId, row);
  const defaultReplacementUserId =
    tempUserDeactivationDraft?.draft?.defaultReplacementUserId || null;
  const defaultReplacementUser =
    (tempUsersData || []).find((user) => user.id === defaultReplacementUserId) ||
    null;
  const hasDefaultReplacement =
    allowReplace &&
    Boolean(defaultReplacementUserId) &&
    isDraftReplacementUserValidForRow(row, defaultReplacementUser, targetUserId);
  const currentValue = getDraftActionValue(row);
  const helperMessage =
    row.itemType === "calendar_event" ||
    row.itemType === "manager_assignment" ||
    row.itemType === "recalls"
      ? ""
      : row.itemType === "approval"
      ? escapeHTML(row.message || "Resolve or reassign this approval before continuing.")
      : row.message;

  if (!allowReplace && !allowRemove && !allowClear) {
    if (!helperMessage) {
      return "";
    }
    return `<p class="text-xs text-gray-600">${helperMessage || "No action available in this phase."}</p>`;
  }

  const options = [];
  if (hasDefaultReplacement) {
    options.push({
      value: "__default__",
      label: `Default replacement (${getDraftUserName(defaultReplacementUserId)})`,
      group: "Options",
    });
  }
  options.push({ value: "__leave__", label: row.details?.required === false ?
    "Leave unchanged" : "Leave unresolved", group: "Options" });
  if (allowRemove) {
    options.push({ value: "__remove__", label: "Remove user", group: "Options" });
  }
  if (allowClear) {
    options.push({ value: "__clear__", label: "Clear default", group: "Options" });
  }
  if (allowReplace) {
    replacementOptions.forEach((user) => {
      options.push({
        value: user.id,
        label: user.name || user.email || user.id,
        group: "Users",
      });
    });
  }

  return `
        <div class="space-y-2">
            ${helperMessage ? `<p class="text-xs text-gray-600">${helperMessage}</p>` : ""}
            <div
              data-draft-row-choice-control
              data-row-id="${attrValue(row.rowId)}"
              data-value="${attrValue(currentValue)}"
              data-options="${attrValue(JSON.stringify(options))}"
              data-label="${attrValue(`Migration action for ${row.itemLabel || row.rowId}`)}"
            ></div>
            <input type="hidden" class="draft-row-choice" data-row-id="${attrValue(row.rowId)}" value="${attrValue(currentValue)}">
        </div>
    `;
}

function getDraftTileLabel(row) {
  if (row?.itemType === "leave_request" && row.details) {
    return formatDraftDetailsDateRange(row.details) || row.itemLabel;
  }
  return row?.itemLabel || "";
}

function getDraftTileNavigation(row) {
  if (row.itemType === "approval" && row.details?.historyFocusId) {
    return {
      action: "drugs-history",
      itemId: row.details.historyFocusId,
    };
  }

  if (
    ["task", "patient", "project", "asset", "lab", "maintenance"]
      .includes(row.itemType)
  ) {
    return {
      action: "details",
      itemId: row.itemId,
      itemType: row.itemType,
    };
  }

  if (["calendar_event", "calendar_reminder"].includes(row.itemType) && row.details?.startDate) {
    return {
      action: "calendar-entry",
      itemId: row.itemId,
      sourceType: row.itemType === "calendar_reminder" ? "reminder" : "event",
      startDate: row.details.startDate,
    };
  }

  if (row.itemType === "leave_request" && row.details?.startDate) {
    return {
      action: "calendar-entry",
      itemId: row.itemId,
      sourceType: "leave",
      startDate: row.details.startDate,
    };
  }

  if (row.itemType === "manager_assignment") {
    return {
      action: "settings-users",
      itemId: row.itemId,
    };
  }

  return null;
}

function buildDraftTiles(sectionKey) {
  const rows = getDraftSectionRows(sectionKey);
  if (sectionKey !== "tasks" && sectionKey !== "patients") {
    return rows
      .map((row) => ({
        tileId: row.rowId,
        itemLabel: getDraftTileLabel(row),
        helperText: row.parentLabel
          ? `${formatParentTypeLabel(row.parentType)}: ${row.parentLabel}`
          : (row.subItemLabel || null),
        rows: [row],
        navigation: getDraftTileNavigation(row),
      }))
      .sort(
        (a, b) => getDraftTileSeverityRank(a.rows) - getDraftTileSeverityRank(b.rows),
      );
  }

  const grouped = new Map();
  rows.forEach((row) => {
    const existing = grouped.get(row.itemId) || {
      tileId: `${sectionKey}:${row.itemId}`,
      itemLabel: getDraftTileLabel(row),
      helperText: row.parentLabel
        ? `${formatParentTypeLabel(row.parentType)}: ${row.parentLabel}`
        : null,
      rows: [],
      navigation: getDraftTileNavigation(row),
    };
    if (!existing.helperText && row.parentLabel) {
      existing.helperText = `${formatParentTypeLabel(row.parentType)}: ${row.parentLabel}`;
    }
    existing.rows.push(row);
    grouped.set(row.itemId, existing);
  });
  return Array.from(grouped.values()).sort(
    (a, b) => getDraftTileSeverityRank(a.rows) - getDraftTileSeverityRank(b.rows),
  );
}

function renderDeactivationDraftFieldRow(row, targetUserId) {
  const helperParts = [];
  if (row.itemType === "approval" && row.details) {
    helperParts.push(`Transaction date: ${formatDraftDate(row.details.transactionDate)}`);
    if (row.details.requestedBy) {
      helperParts.push(`Submitted by: ${getDraftUserName(row.details.requestedBy)}`);
    }
    if (row.details.locationLabel) {
      helperParts.push(`Location: ${row.details.locationLabel}`);
    }
  }
  if (row.itemType === "task" && row.details) {
    helperParts.push(
      `Task owner: ${row.details.ownerId ? getDraftUserName(row.details.ownerId) : "None"}`,
    );
    helperParts.push(
      `Assigned users: ${formatDraftUserList(row.details.assignedUserIds || [])}`,
    );
  }
  if (row.itemType === "patient" && row.details) {
    helperParts.push(
      `Case manager: ${row.details.caseManagerId ? getDraftUserName(row.details.caseManagerId) : "None"}`,
    );
    helperParts.push(`Providers: ${formatDraftUserList(row.details.providerIds || [])}`);
  }
  if (row.itemType === "project") {
    helperParts.push(`Project managers: ${formatDraftUserList(row.currentUserIds || [])}`);
  }
  if (row.itemType === "lab" && row.details) {
    helperParts.push(
      `Types: ${escapeHTML(formatDraftTypeList(
        row.details.typeIds || [],
        "labTypes",
      ))}`,
    );
    helperParts.push(
      `Assigned users: ${formatDraftUserList(row.details.assignedUserIds || [])}`,
    );
    helperParts.push(`Providers: ${formatDraftUserList(row.details.providerIds || [])}`);
  }
  if (row.itemType === "recalls" && row.details) {
    const count = row.details.affectedCount || 0;
    helperParts.push(`Active recalls affected: ${count}`);
  }
  if (row.itemType === "maintenance" && row.details) {
    helperParts.push(
      `Managed by: ${row.details.managedById ? getDraftUserName(row.details.managedById) : "None"}`,
    );
  }
  if (row.itemType === "asset") {
    if (row.subItemLabel) {
      helperParts.push(`Issue: ${row.subItemLabel}`);
    }
    helperParts.push(`Managed by: ${formatDraftUserList(row.currentUserIds || [])}`);
  }
  if (row.itemType === "config") {
    helperParts.push(`Current user: ${formatDraftUserList(row.currentUserIds || [])}`);
  }
  if (row.itemType === "team") {
    helperParts.push(`Team members: ${formatDraftUserList(row.currentUserIds || [])}`);
  }
  if (row.itemType === "compliance_person" && row.details) {
    helperParts.push(
      `Source state: ${escapeHTML(row.details.sourceState || "active")}`,
    );
    helperParts.push(
      `Compliance history: ${row.details.hasHistory ? "retained" : "empty"}`,
    );
    helperParts.push(
      `Reminder suppression: ${
        row.details.remindersSuppressed ? "active" : "applied on deactivation"
      }`,
    );
  }
  if (row.itemType === "calendar_event" && row.details) {
    const dateRange = formatDraftDetailsDateRange(row.details);
    if (dateRange) {
      helperParts.push(`Dates: ${escapeHTML(dateRange)}`);
    }
    helperParts.push(`Hosts: ${formatDraftUserList(row.details.hostIds || [])}`);
    helperParts.push(
      `Attendees: ${formatDraftUserList(row.details.attendeeIds || [])}`,
    );
  }
  if (row.itemType === "leave_request" && row.details) {
    helperParts.push(
      `Approvers: ${formatDraftUserList(row.details.approverIds || [])}`,
    );
  }
  if (row.itemType === "manager_assignment" && row.details) {
    const userNames = Array.isArray(row.details.affectedUserNames) ?
      row.details.affectedUserNames :
      [];
    const userList = userNames.length ?
      userNames.map((name) => escapeHTML(name)).join(", ") :
      formatDraftUserList(row.details.affectedUserIds || []);
    helperParts.push(`Users: ${userList}`);
  }
  if (row.itemType === "approval") {
    helperParts.push(`Approvers: ${formatDraftUserList(row.currentUserIds || [])}`);
    if (row.details?.leaveRequestId) {
      helperParts.push(
        `Leave dates: ${escapeHTML(formatDraftDetailsDateRange(row.details))}`,
      );
    }
  }
  if (row.itemType === "user" && row.field === "role") {
    helperParts.push(`Current user: ${formatDraftUserList(row.currentUserIds || [])}`);
  }
  return `
        <div class="grid grid-cols-1 gap-3 rounded-lg border border-white/60 bg-white/70 p-4 md:grid-cols-[minmax(0,2fr)_minmax(260px,1fr)]">
            <div>
                <div class="text-sm font-semibold text-gray-900">${getDraftFieldLabel(row.field, row)}</div>
                ${helperParts.length > 0 ? `<div class="mt-2 space-y-1 text-xs text-gray-600">${helperParts.map((part) => `<div>${part}</div>`).join("")}</div>` : ""}
            </div>
            <div class="deactivation-draft-control">
                ${renderDeactivationDraftActionControls(row, targetUserId)}
            </div>
        </div>
    `;
}

function renderDeactivationDraftTile(tile, targetUserId) {
  const severity = getDraftTileSeverity(tile.rows);
  const navData = tile.navigation;
  const navAttrs = navData ?
    [
      `data-nav-action="${attrValue(navData.action)}"`,
      `data-item-id="${attrValue(navData.itemId)}"`,
      navData.itemType ?
        `data-item-type="${attrValue(navData.itemType)}"` :
        "",
      navData.sourceType ?
        `data-source-type="${attrValue(navData.sourceType)}"` :
        "",
      navData.startDate ?
        `data-start-date="${attrValue(navData.startDate)}"` :
        "",
    ].filter(Boolean).join(" ") :
    "";
  const clickableClass = navData
    ? "cursor-pointer hover:shadow-md transition-shadow"
    : "";

  return `
        <article class="deactivation-draft-tile rounded-xl border p-4 ${severity.wrapper} ${clickableClass}" ${navAttrs}>
            <div>
                <div>
                    <h5 class="text-base font-semibold text-gray-900">${tile.itemLabel}</h5>
                    ${tile.helperText ? `<div class="mt-1 text-xs text-gray-600">${tile.helperText}</div>` : ""}
                </div>
            </div>
            <div class="mt-4 space-y-3">
                ${tile.rows.map((row) => renderDeactivationDraftFieldRow(row, targetUserId)).join("")}
            </div>
        </article>
    `;
}

function renderDeactivationDraftSection(key, label, targetUserId) {
  const tiles = buildDraftTiles(key);
  if (!tiles.length) {
    return "";
  }

  return `
        <section class="space-y-3">
            <div class="flex items-center gap-3">
                <h4 class="text-base font-semibold text-gray-900">${label}</h4>
                <span class="rounded-full bg-gray-100 px-2 py-0.5 text-xs text-gray-600">${tiles.length}</span>
            </div>
            <div class="space-y-3">
                ${tiles.map((tile) => renderDeactivationDraftTile(tile, targetUserId)).join("")}
            </div>
        </section>
    `;
}

/**
 * Renders the active user deactivation draft workspace.
 * @returns {string} Draft workspace HTML.
 */
export function renderDeactivationDraftContainer() {
  if (!tempUserDeactivationDraft?.draft?.targetUserId) {
    return "";
  }
  if (hiddenDeactivationDraftUserId === tempUserDeactivationDraft.draft.targetUserId) {
    return "";
  }

  const accessReview = tempUserDeactivationDraft.draft.operation === "access_change";
  const targetUser = tempUserDeactivationDraft.targetUser;
  const summary = tempUserDeactivationDraft.summary || {};
  const requiredRowsRemaining = Object.values(
    tempUserDeactivationDraft.sections || {},
  )
    .flat()
    .filter((row) => doesDraftRowRequireMigration(row)).length;
  const sections = [
    ["safety", "Safety"],
    ["configDefaults", "Config Defaults"],
    ["tasks", "Tasks"],
    ["patients", "Patients"],
    ["projects", "Projects"],
    ["assetIssues", "Asset Issues"],
    ["labs", "Labs"],
    ["maintenance", "Maintenance"],
    ["approvals", "Approvals"],
    ["recalls", "Recalls"],
    ["calendarEvents", "Calendar Events"],
    ["calendarReminders", "Calendar Reminders"],
    ["leave", "Leave"],
    ["managerAssignments", "Manager Assignments"],
    ["compliancePeople", "Compliance People"],
    ["teams", "Teams"],
  ];
  const targetUserId = tempUserDeactivationDraft.draft.targetUserId;
  const executableRowCount = getDraftExecutableRowCount();
  const saveHint = state.hasUnsavedChanges
    ? '<span class="text-xs text-amber-700">Unsaved draft changes</span>'
    : '<span class="text-xs text-gray-500">Draft saved</span>';

  const defaultReplacementOptions = getDraftReplacementSelectOptions(targetUserId);
  const defaultReplacementValue =
    tempUserDeactivationDraft.draft.defaultReplacementUserId || "";

  return `
        <div id="user-deactivation-draft" class="deactivation-draft-workspace mt-6 rounded-xl border border-gray-200 bg-white p-6 shadow-sm">
            <div class="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
                <div>
                    <h3 tabindex="-1" class="text-xl font-semibold text-gray-900">${accessReview ? "Review access changes for" : "Deactivating"} ${escapeHTML(targetUser?.name || getDraftUserName(targetUserId))}</h3>
                    ${accessReview ? `<p class="mt-2 text-sm text-gray-600">Pending: ${escapeHTML(describeUserAccessEdit(tempUserDeactivationDraft.draft.pendingUserSettings))}. Permissions stay unchanged until Apply access changes. Reassignments you run are saved immediately and retained if you cancel this review.</p>` : ""}
                    <div class="mt-1 text-sm text-gray-600">
                        ${summary.blockerCount || 0} blockers, ${summary.workloadCount || 0} active workload items, ${summary.teamMembershipCount || 0} team memberships
                    </div>
                </div>
                <div class="text-sm text-gray-600">
                    ${saveHint}
                </div>
            </div>

            <div class="mt-4 rounded-lg border border-gray-200 bg-white p-4">
                <label class="block text-sm font-medium text-gray-700" for="draft-default-replacement">Default replacement user</label>
                <div
                  class="mt-2 w-full max-w-md"
                  data-draft-default-replacement-control
                  data-value="${attrValue(defaultReplacementValue)}"
                  data-options="${attrValue(JSON.stringify(defaultReplacementOptions))}"
                ></div>
                <input type="hidden" id="draft-default-replacement" value="${attrValue(defaultReplacementValue)}">
            </div>

            <div class="mt-6 space-y-6">
                ${sections.map(([key, label]) => renderDeactivationDraftSection(key, label, targetUserId)).join("") || '<p class="text-sm text-gray-500">No draft sections to display.</p>'}
            </div>

            <div class="deactivation-draft-actionbar mt-6 flex flex-wrap items-center gap-3">
                <button id="deactivation-draft-cancel-btn" class="settings-danger-action-btn">${accessReview ? "Cancel access review" : "Cancel Migration"}</button>
                <button id="deactivation-draft-refresh-btn" class="settings-neutral-action-btn" data-user-id="${attrValue(targetUserId)}">Refresh</button>
                ${executableRowCount > 0 || requiredRowsRemaining > 0 ? `<button id="deactivation-draft-run-btn" class="settings-primary-action-btn" data-user-id="${attrValue(targetUserId)}" ${executableRowCount ? "" : "disabled"}>Run Migration${executableRowCount ? ` (${executableRowCount})` : ""}</button>` : ""}
                ${requiredRowsRemaining === 0 ? `<button id="deactivation-draft-finalize-btn" class="${accessReview ? "settings-primary-action-btn" : "settings-danger-action-btn"}" data-user-id="${attrValue(targetUserId)}" ${executableRowCount ? 'disabled title="Run or clear selected reassignments first"' : ""}>${accessReview ? "Apply access changes" : "Deactivate User"}</button>` : ""}
            </div>
        </div>
    `;
}

function parseDraftSelectOptions(value) {
  try {
    const options = JSON.parse(value || "[]");
    return Array.isArray(options) ? options : [];
  } catch (error) {
    console.warn("Failed to parse deactivation draft select options:", error);
    return [];
  }
}

/**
 * Mounts designed controls used inside the deactivation draft workspace.
 * @param {HTMLElement} container Users tab container.
 */
export function setupUserDeactivationDraftControls(container) {
  const draftRoot = container.querySelector("#user-deactivation-draft");
  if (!draftRoot) {
    return;
  }

  const defaultRoot = draftRoot.querySelector("[data-draft-default-replacement-control]");
  const defaultMirror = draftRoot.querySelector("#draft-default-replacement");
  if (defaultRoot && defaultMirror instanceof HTMLInputElement) {
    createSelectControl({
      root: defaultRoot,
      mirrorInput: defaultMirror,
      options: parseDraftSelectOptions(defaultRoot.dataset.options),
      value: defaultRoot.dataset.value || "",
      searchable: true,
      searchThreshold: 6,
      clearable: false,
      portalMenu: true,
      placeholder: "No default replacement",
      accessibleName: "Default replacement user",
    });
  }

  draftRoot.querySelectorAll("[data-draft-row-choice-control]").forEach((root) => {
    const rowId = root.dataset.rowId || "";
    const mirrorInput = Array.from(draftRoot.querySelectorAll(".draft-row-choice"))
      .find((input) => input.dataset.rowId === rowId);
    if (!(mirrorInput instanceof HTMLInputElement)) {
      return;
    }

    createSelectControl({
      root,
      mirrorInput,
      options: parseDraftSelectOptions(root.dataset.options),
      value: root.dataset.value || "__leave__",
      searchable: true,
      searchThreshold: 6,
      clearable: false,
      portalMenu: true,
      placeholder: "Select action",
      accessibleName: root.dataset.label || "Migration action",
    });
  });
}

function applyDraftSelection(rowId, value) {
  if (!rowId || !tempUserDeactivationDraft?.draft) {
    return;
  }

  if (!tempUserDeactivationDraft.draft.selections) {
    tempUserDeactivationDraft.draft.selections = {};
  }

  if (value === "__leave__") {
    tempUserDeactivationDraft.draft.selections[rowId] = {
      action: "leave_unresolved",
      replacementUserId: null,
    };
    return;
  }

  if (value === "__remove__") {
    tempUserDeactivationDraft.draft.selections[rowId] = {
      action: "remove",
      replacementUserId: null,
    };
    return;
  }

  if (value === "__clear__") {
    tempUserDeactivationDraft.draft.selections[rowId] = {
      action: "clear",
      replacementUserId: null,
    };
    return;
  }

  if (value === "__default__") {
    tempUserDeactivationDraft.draft.selections[rowId] = {
      action: "replace",
      replacementUserId: null,
    };
    return;
  }

  tempUserDeactivationDraft.draft.selections[rowId] = {
    action: "replace",
    replacementUserId: value || null,
  };
}

/**
 * Handles deactivation draft form changes.
 * @param {HTMLElement} target Event target.
 * @param {{ rerenderUsersTab?: Function, updateSaveButton?: Function }} callbacks Coordinator callbacks.
 * @returns {boolean} True when handled.
 */
export function handleUserDeactivationSettingsChange(target, callbacks = {}) {
  const rerenderUsersTab = callbacks.rerenderUsersTab || (() => {});
  const updateSaveButton = callbacks.updateSaveButton || (() => {});

  if (target.id === "draft-default-replacement" && tempUserDeactivationDraft?.draft) {
    tempUserDeactivationDraft.draft.defaultReplacementUserId = target.value || null;
    rerenderUsersTab();
    updateSaveButton(true);
    return true;
  }

  if (target.matches(".draft-row-choice") && tempUserDeactivationDraft?.draft) {
    applyDraftSelection(target.dataset.rowId, target.value);
    rerenderUsersTab();
    updateSaveButton(true);
    return true;
  }

  return false;
}

/**
 * Syncs draft selections from the current DOM into temp state before save.
 */
export function syncUserDeactivationSettingsStateFromDom() {
  if (!tempUserDeactivationDraft?.draft) {
    return;
  }

  const draftRoot = document.getElementById("user-deactivation-draft");
  if (!draftRoot) {
    return;
  }

  const defaultReplacementSelect = draftRoot.querySelector(
    "#draft-default-replacement",
  );
  if (defaultReplacementSelect) {
    tempUserDeactivationDraft.draft.defaultReplacementUserId =
      defaultReplacementSelect.value || null;
  }

  draftRoot.querySelectorAll(".draft-row-choice").forEach((select) => {
    applyDraftSelection(select.dataset.rowId, select.value);
  });
}

/**
 * Applies deactivation draft post-save state updates.
 */
export function handleUserDeactivationSettingsSaveSuccess() {
  if (tempUserDeactivationDraft?.draft) {
    upsertDeactivationDraftSummary(tempUserDeactivationDraft);
  }
}

/**
 * Resets local deactivation draft UI state.
 */
export function resetUserDeactivationSettingsState() {
  deactivationDraftsLoading = null;
  deactivationDraftsLoaded = false;
  hiddenDeactivationDraftUserId = null;
  clearDeactivationDraftSummaries();
}

async function openDraftCalendarEntryNavigation({
  sourceType,
  sourceId,
  startDate,
  title,
}) {
  if (!sourceType || !sourceId) {
    return;
  }
  state.settingsReturnContext = {
    tab: "users",
    deactivationDraftTargetUserId:
      tempUserDeactivationDraft?.draft?.targetUserId || null,
  };
  navigate("calendar", startDate ? `day/${startDate}` : null);
  if (sourceType === "event") {
    const { openEventDetailModal } =
      await import("../calendar/eventDetailModal.js");
    openEventDetailModal({ sourceType, sourceId, title });
  }
  if (sourceType === "leave") {
    const { openLeaveDetailModal } =
      await import("../calendar/leaveDetailModal.js");
    openLeaveDetailModal({ sourceType, sourceId, title });
  }
}

/**
 * Handles deactivation draft and lifecycle action buttons.
 * @param {HTMLElement} target Event target.
 * @param {{ rerenderUsersTab?: Function, updateSaveButton?: Function }} callbacks Coordinator callbacks.
 * @returns {Promise<boolean>} True when handled.
 */
export async function handleUserDeactivationSettingsButtonClick(target, callbacks = {}) {
  const rerenderUsersTab = callbacks.rerenderUsersTab || (() => {});
  const updateSaveButton = callbacks.updateSaveButton || (() => {});
  const tile = target.closest(".deactivation-draft-tile");
  if (
    tile &&
    !target.closest(".deactivation-draft-control") &&
    !target.closest("button, select, input")
  ) {
    const navAction = tile.dataset.navAction;
    const itemId = tile.dataset.itemId;
    const itemType = tile.dataset.itemType || null;
    const sourceType = tile.dataset.sourceType || "";
    const startDate = tile.dataset.startDate || "";

    if (navAction === "details" && itemId) {
      state.settingsReturnContext = {
        tab: "users",
        deactivationDraftTargetUserId:
          tempUserDeactivationDraft?.draft?.targetUserId || null,
      };
      navigateToDetails(itemId, itemType);
      return true;
    }

    if (navAction === "drugs-history" && itemId) {
      state.settingsReturnContext = {
        tab: "users",
        deactivationDraftTargetUserId:
          tempUserDeactivationDraft?.draft?.targetUserId || null,
      };
      state.drugHistoryFocusId = itemId;
      navigate("drugs", "history");
      return true;
    }

    if (navAction === "calendar-entry" && itemId && sourceType) {
      await openDraftCalendarEntryNavigation({
        sourceType,
        sourceId: itemId,
        startDate,
        title: tile.querySelector("h5")?.textContent?.trim() || "",
      });
      return true;
    }

    if (navAction === "settings-users") {
      hiddenDeactivationDraftUserId =
        tempUserDeactivationDraft?.draft?.targetUserId || null;
      rerenderUsersTab();
      state.settingsReturnContext = { tab: "users" };
      navigate("settings");
      return true;
    }
  }

  const openDraftButton = target.closest(".btn-open-deactivation-draft, .btn-open-access-review");
  if (openDraftButton) {
    const userId = openDraftButton.dataset.userId;
    if (!userId) {
      return true;
    }

    const originalHtml = openDraftButton.innerHTML;
    openDraftButton.disabled = true;
    openDraftButton.innerHTML =
      '<span class="material-symbols-outlined text-[18px] animate-spin">progress_activity</span>';

    try {
      const draftPayload = openDraftButton.matches(".btn-open-access-review") ?
        await refreshUserDeactivationDraft(userId, {operation: "access_change",
          draftId: openDraftButton.dataset.draftId}) :
        await openUserDeactivationDraft(userId);
      hiddenDeactivationDraftUserId = null;
      setTempUserDeactivationDraft(draftPayload);
      upsertDeactivationDraftSummary(draftPayload);
      rerenderUsersTab();
      document.getElementById("user-deactivation-draft")?.scrollIntoView({block: "start"});
      document.querySelector("#user-deactivation-draft h3")?.focus();
    } catch (error) {
      console.error("Failed to open deactivation draft:", error);
      openConfirmationModal(
        "Error",
        error.message || "Could not open the deactivation workflow.",
        () => {},
        false,
        "OK",
      );
      openDraftButton.disabled = false;
      openDraftButton.innerHTML = originalHtml;
    }
    return true;
  }

  if (target.closest("#deactivation-draft-cancel-btn")) {
    const targetUserId = tempUserDeactivationDraft?.draft?.targetUserId || null;
    if (!targetUserId) {
      return true;
    }

    openConfirmationModal(
      isAccessReview() ? "Cancel access review" : "Cancel Migration",
      isAccessReview() ?
        "Cancel the pending access changes? Reassignments already run remain saved." :
        "Cancel this deactivation draft? Reassignments already run remain saved.",
      async () => {
        try {
          await deleteUserDeactivationDraft(targetUserId, currentReviewContext());
          removeDeactivationDraftSummary(targetUserId, currentReviewContext().operation);
          hiddenDeactivationDraftUserId = null;
          clearTempUserDeactivationDraft();
          rerenderUsersTab();
        } catch (error) {
          console.error("Failed to delete deactivation draft:", error);
          openConfirmationModal(
            "Error",
            error.message || "Could not delete the deactivation draft.",
            () => {},
            false,
            "OK",
          );
        }
      },
      true,
      isAccessReview() ? "Cancel review" : "Delete Draft",
    );
    return true;
  }

  const refreshDraftButton = target.closest("#deactivation-draft-refresh-btn");
  if (refreshDraftButton) {
    const userId = refreshDraftButton.dataset.userId;
    if (!userId) {
      return true;
    }

    const originalText = refreshDraftButton.textContent;
    refreshDraftButton.disabled = true;
    refreshDraftButton.textContent = "Refreshing...";

    try {
      const draftPayload = await refreshUserDeactivationDraft(userId, currentReviewContext());
      hiddenDeactivationDraftUserId = null;
      setTempUserDeactivationDraft(draftPayload);
      upsertDeactivationDraftSummary(draftPayload);
      rerenderUsersTab();
    } catch (error) {
      console.error("Failed to refresh deactivation draft:", error);
      openConfirmationModal(
        "Error",
        error.message || "Could not refresh the deactivation workflow.",
        () => {},
        false,
        "OK",
      );
      refreshDraftButton.disabled = false;
      refreshDraftButton.textContent = originalText;
    }
    return true;
  }

  const runDraftButton = target.closest("#deactivation-draft-run-btn");
  if (runDraftButton) {
    const userId = runDraftButton.dataset.userId;
    if (!userId) {
      return true;
    }

    openConfirmationModal(
      "Run Migration",
      "Run all selected reassignment actions now? Items set to Leave unresolved will remain in the draft.",
      async () => {
        const originalText = runDraftButton.textContent;
        runDraftButton.disabled = true;
        runDraftButton.textContent = "Running...";

        try {
          syncUserDeactivationSettingsStateFromDom();
          const saveSuccess = isAccessReview() ?
            await saveAccessReviewSelections() : await saveSettingsChanges();
          if (!saveSuccess) {
            runDraftButton.disabled = false;
            runDraftButton.textContent = originalText;
            return;
          }

          const draftPayload = await runUserDeactivationMigration(userId, currentReviewContext());
          hiddenDeactivationDraftUserId = null;
          setTempUserDeactivationDraft(draftPayload);
          upsertDeactivationDraftSummary(draftPayload);
          rerenderUsersTab();
          updateSaveButton(isAccessReview() ? state.hasUnsavedChanges : false);

          const result = draftPayload?.migrationResult || {};
          const processedRowCount = result.processedRowCount || 0;
          const skippedRowCount = result.skippedRowCount || 0;
          const updatedDocumentCount = result.updatedDocumentCount || 0;
          const summaryHtml = `
                        Processed rows: <strong>${processedRowCount}</strong><br>
                        Updated items: <strong>${updatedDocumentCount}</strong>
                        ${skippedRowCount > 0 ? `<br>Skipped rows: <strong>${skippedRowCount}</strong>` : ""}
                    `;
          setTimeout(() => {
            openConfirmationModal(
              "Migration Complete",
              summaryHtml,
              () => {},
              false,
              "OK",
            );
          }, 50);
        } catch (error) {
          console.error("Failed to run deactivation migration:", error);
          setTimeout(() => {
            openConfirmationModal(
              "Error",
              error.message || "Could not run the deactivation migration.",
              () => {},
              false,
              "OK",
            );
          }, 50);
        } finally {
          runDraftButton.disabled = false;
          runDraftButton.textContent = originalText;
        }
      },
      true,
      "Run Migration",
    );
    return true;
  }

  const finalizeDraftButton = target.closest("#deactivation-draft-finalize-btn");
  if (finalizeDraftButton) {
    const userId = finalizeDraftButton.dataset.userId;
    if (!userId) {
      return true;
    }

    if (isAccessReview()) {
      finalizeDraftButton.disabled = true;
      try {
        const result = (await applyUserAccessChangeCallable({targetUserId: userId,
          draftId: tempUserDeactivationDraft.draft.id})).data;
        if (result?.success !== true) {throw new Error("Access changes did not complete.");}
        applyConfirmedUserSettings(result.user);
        removeDeactivationDraftSummary(userId, "access_change");
        clearTempUserDeactivationDraft();
        rerenderUsersTab();
        updateSaveButton(state.hasUnsavedChanges);
      } catch (error) {
        openConfirmationModal("Access changes not applied", error.message,
          () => {}, false, "OK");
        finalizeDraftButton.disabled = false;
      }
      return true;
    }

    openConfirmationModal(
      "Deactivate User",
      "Deactivate this user now? This will clear their access permissions, remove them from all teams, revoke their active sessions, and finalise this deactivation record.",
      async () => {
        const originalText = finalizeDraftButton.textContent;
        finalizeDraftButton.disabled = true;
        finalizeDraftButton.textContent = "Deactivating...";

        try {
          syncUserDeactivationSettingsStateFromDom();
          const saveSuccess = isAccessReview() ?
            await saveAccessReviewSelections() : await saveSettingsChanges();
          if (!saveSuccess) {
            finalizeDraftButton.disabled = false;
            finalizeDraftButton.textContent = originalText;
            return;
          }

          await deactivateUserWithDraft(userId);
          const nextUsers = (tempUsersData || []).map((user) =>
            user.id === userId
              ? {
                  ...user,
                  isActive: false,
                  role: "user",
                  permissions: [],
                  teams: [],
                  isAppProvider: false,
                  drugRole: null,
                  isDrugAdmin: false,
                  isProvider: false,
                }
              : user,
          );
          setTempUsersData(nextUsers);
          hiddenDeactivationDraftUserId = null;
          clearTempUserDeactivationDraft();
          removeDeactivationDraftSummary(userId);
          rerenderUsersTab();
          updateSaveButton(false);

          setTimeout(() => {
            openConfirmationModal(
              "User Deactivated",
              "The user has been deactivated and their access has been cleared.",
              () => {},
              false,
              "OK",
            );
          }, 50);
        } catch (error) {
          console.error("Failed to deactivate user:", error);
          setTimeout(() => {
            openConfirmationModal(
              "Error",
              error.message || "Could not deactivate the user.",
              () => {},
              false,
              "OK",
            );
          }, 50);
        } finally {
          finalizeDraftButton.disabled = false;
          finalizeDraftButton.textContent = originalText;
        }
      },
      true,
      "Deactivate User",
    );
    return true;
  }

  const reactivateButton = target.closest(".btn-reactivate-user");
  if (reactivateButton) {
    const userId = reactivateButton.dataset.userId;
    if (!userId) {
      return true;
    }

    openConfirmationModal(
      "Reactivate User",
      "Reactivate this user? Their account will become active again, but permissions, teams, and roles will need to be reassigned manually.",
      async () => {
        const originalHtml = reactivateButton.innerHTML;
        reactivateButton.disabled = true;
        reactivateButton.innerHTML =
          '<span class="material-symbols-outlined text-[18px] animate-spin">progress_activity</span>';

        try {
          await reactivateUser(userId);
          const nextUsers = (tempUsersData || []).map((user) =>
            user.id === userId ? { ...user, isActive: true } : user,
          );
          setTempUsersData(nextUsers);
          rerenderUsersTab();
          updateSaveButton(false);
          setTimeout(() => {
            openConfirmationModal(
              "User Reactivated",
              "The user is active again. Reassign permissions, teams, and roles manually as needed.",
              () => {},
              false,
              "OK",
            );
          }, 50);
        } catch (error) {
          console.error("Failed to reactivate user:", error);
          setTimeout(() => {
            openConfirmationModal(
              "Error",
              error.message || "Could not reactivate the user.",
              () => {},
              false,
              "OK",
            );
          }, 50);
        } finally {
          reactivateButton.disabled = false;
          reactivateButton.innerHTML = originalHtml;
        }
      },
      false,
      "Reactivate",
    );
    return true;
  }
  return false;
}

/**
 * Identifies the active shared workspace operation.
 * @returns {boolean} Whether it reviews access only.
 */
function isAccessReview() {
  return tempUserDeactivationDraft?.draft?.operation === "access_change";
}

/**
 * Shapes identity for shared lifecycle actions.
 * @returns {object} Exact operation and review identity.
 */
function currentReviewContext() {
  return {operation: tempUserDeactivationDraft?.draft?.operation || "deactivation",
    draftId: tempUserDeactivationDraft?.draft?.id};
}

/**
 * Persists access review selections independently of other Settings edits.
 * @returns {Promise<boolean>} Whether selections were saved.
 */
async function saveAccessReviewSelections() {
  const draft = tempUserDeactivationDraft.draft;
  const saved = await saveUserDeactivationDraft(draft.targetUserId, {
    ...currentReviewContext(), selections: draft.selections,
    defaultReplacementUserId: draft.defaultReplacementUserId,
  });
  setTempUserDeactivationDraft(saved);
  return true;
}
